import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCircle } from '@osd/icons/alert-circle';
import { Alert } from '@osd/ui/alert';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Checkbox } from '@osd/ui/checkbox';
import { ColorPicker } from '@osd/ui/color-picker';
import { ErrorSummary } from '@osd/ui/error-summary';
import { Fieldset } from '@osd/ui/fieldset';
import { Field } from '@osd/ui/field';
import { FileInput } from '@osd/ui/file-input';
import { Heading } from '@osd/ui/heading';
import { Input } from '@osd/ui/input';
import { InputGroup } from '@osd/ui/input-group';
import { Range } from '@osd/ui/range';
import { Radio } from '@osd/ui/radio';
import { Select } from '@osd/ui/select';
import { Stack } from '@osd/ui/stack';
import { Switch } from '@osd/ui/switch';
import { Text } from '@osd/ui/text';
import { Textarea } from '@osd/ui/textarea';
import { useForm } from '@osd/ui/use-form';

import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/form — the form surface for `@osd/ui`.
 *
 * One stop for everything a consuming app needs to build a form:
 *
 *   1. Primitives — `<Field>`, `<Input>`, `<Textarea>`, `<Select>`,
 *      `<Checkbox>`, `<Radio>`, `<Switch>`, `<Fieldset>`,
 *      `<ErrorSummary>`. All primitives are uncontrolled-friendly
 *      (each carries a `name` attribute) but also support a
 *      controlled mode via `value` + `onChange` + `onBlur`.
 *
 *   2. Handling — `useForm()` is the project's form-state hook.
 *      No external dep; ships values / errors / touched / dirty +
 *      handleChange / handleBlur / setFieldValue / setFieldError /
 *      handleSubmit / reset / submitting. Optional `validate(values)`
 *      runs on every change and on submit; return an empty object
 *      to pass.
 *
 * The page is intentionally a single doc with several demos. Each
 * demo is wrapped in its own `<Card>` so the consumer can copy the
 * pattern they need and ignore the rest.
 */
export const Route = createFileRoute('/ui/_layout/form')({
  component: FormPage,
});

interface Demo {
  readonly title: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}

function DemoShell({ title, hint, children }: Demo) {
  return (
    <Card>
      <Stack gap={3}>
        <header>
          <Heading level={3}>{title}</Heading>
          {hint ? <Text muted>{hint}</Text> : null}
        </header>
        {children}
      </Stack>
    </Card>
  );
}

function FormPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Form"
        hint="primitives + useForm() for state handling — see root/osd#75 for why we no longer ship an app-level composite"
      >
        <Card>
          <Card.Title>TL;DR</Card.Title>
          <Card.Body>
            <Stack gap={3}>
              <Text>
                Wrap every form control in a <code>&lt;Field label="…" required&gt;</code> so the
                label auto-binds to the input's id and the field renders a consistent label / hint /
                error layout. Drive state with either uncontrolled inputs (just give them a{' '}
                <code>name</code>) or with a controlled form via <code>useForm()</code> when you
                need validation, error tracking, and a single submit handler.
              </Text>
              <Text muted>
                The next two demos show <code>useForm()</code> in action with both uncontrolled and
                controlled inputs. (The form page itself used to demonstrate a working
                <code>&lt;LoginForm&gt;</code> composite — that composite was an app-level
                shortcode, not part of
                <code>@osd/ui</code>, and has been removed; see root/osd#75.)
              </Text>
            </Stack>
          </Card.Body>
        </Card>
      </Section>

      <Section
        title="useForm — uncontrolled inputs"
        hint="values + errors + touched + dirty; client-side validate on every change"
      >
        <UncontrolledDemo />
      </Section>

      <Section
        title="useForm — controlled inputs"
        hint="same hook, but with explicit value / onChange / onBlur wiring"
      >
        <ControlledDemo />
      </Section>

      <Section
        title="Other form primitives"
        hint="checkbox, radio, switch, select, textarea, fieldset, error-summary"
      >
        <Card>
          <Card.Body>
            <Stack gap={4}>
              <DemoShell title="Textarea">
                <Textarea name="bio" placeholder="A short bio…" rows={3} />
              </DemoShell>
              <DemoShell title="Select">
                <Field label="Role" required>
                  <Select name="role" defaultValue="member">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </Select>
                </Field>
              </DemoShell>
              <DemoShell title="Checkbox + Radio + Switch">
                <Field label="Notifications">
                  <Checkbox name="notif-email" label="Email" defaultChecked />
                  <Checkbox name="notif-sms" label="SMS" />
                </Field>
                <Field label="Theme">
                  <Radio name="theme" value="light" label="Light" defaultChecked />
                  <Radio name="theme" value="dark" label="Dark" />
                </Field>
                <Switch name="marketing" label="Send me product updates" />
              </DemoShell>
              <DemoShell title="ColorPicker — default + random-roll variant">
                <ColorPicker label="Brand color" defaultValue="#0ea5e9" />
                <ColorPicker label="Accent (random)" defaultValue="#0ea5e9" variant="random" />
              </DemoShell>
              <DemoShell title="InputGroup — prefix/suffix addons fused into one control">
                <InputGroup>
                  <InputGroup.Addon>@</InputGroup.Addon>
                  <Input name="handle" placeholder="username" />
                </InputGroup>
                <InputGroup>
                  <InputGroup.Addon>https://example.com/users/</InputGroup.Addon>
                  <Input name="slug" placeholder="vanity-url" />
                </InputGroup>
                <InputGroup>
                  <Input name="price" type="number" placeholder="0.00" />
                  <InputGroup.Addon>USD</InputGroup.Addon>
                </InputGroup>
              </DemoShell>
              <DemoShell title="Range — slider with optional caption + value display">
                <Range
                  label="Volume"
                  hint="0 = muted, 100 = max"
                  min={0}
                  max={100}
                  defaultValue={50}
                  showValue
                  name="volume"
                />
                <Range
                  label="Opacity"
                  min={0}
                  max={1}
                  step={0.05}
                  defaultValue={0.85}
                  name="opacity"
                />
              </DemoShell>
              <DemoShell title="FileInput — file picker with filename + clear">
                <FileInput
                  label="Avatar"
                  accept="image/*"
                  name="avatar"
                  hint="PNG, JPG up to 5 MB"
                />
                <FileInput label="Attachments" multiple name="attachments" />
              </DemoShell>
              <DemoShell title="Fieldset (grouped controls)">
                <Fieldset legend="Address">
                  <Field label="Street">
                    <Input name="street" />
                  </Field>
                  <Field label="City">
                    <Input name="city" />
                  </Field>
                </Fieldset>
              </DemoShell>
              <DemoShell title="ErrorSummary (top-of-form errors)">
                <ErrorSummary
                  errors={[
                    {
                      fieldId: 'email',
                      fieldLabel: 'Email',
                      message: 'Enter a valid email.',
                    },
                    {
                      fieldId: 'password',
                      fieldLabel: 'Password',
                      message: 'At least 12 characters.',
                    },
                  ]}
                />
              </DemoShell>
            </Stack>
          </Card.Body>
        </Card>
      </Section>

      <Section title="Handling — validation, errors, submit">
        <Card>
          <Card.Body>
            <Stack gap={3}>
              <Text>
                <strong>Validation:</strong> pass <code>validate(values)</code> to{' '}
                <code>useForm()</code>. It runs on every change AND on submit. Return{' '}
                <code>{`{}`}</code> when valid; any field key → error message becomes{' '}
                <code>form.errors[key]</code>.
              </Text>
              <Text>
                <strong>Server errors:</strong> the route handler sets <code>auth.error</code> after
                a failed <code>POST /api/auth/login</code>, and{' '}
                <code>
                  &lt;Alert variant={'danger'}&gt;{'{auth.error}'}&lt;/Alert&gt;
                </code>{' '}
                surfaces it inside the card. For server-side field errors (e.g. "email already in
                use"), call <code>form.setFieldError('email', msg)</code> from the submit handler.
              </Text>
              <Text>
                <strong>Submit:</strong> <code>form.handleSubmit</code> prevents default, runs{' '}
                <code>validate</code>, blocks on errors (sets <code>form.touched</code> so the
                errors render), then calls <code>onSubmit(values)</code>. Set{' '}
                <code>form.submitting</code> true around the await — it disables the submit button
                via <code>&lt;Button disabled={'{form.submitting}'} /&gt;</code>.
              </Text>
              <Text>
                <strong>Reset:</strong> <code>form.reset()</code> restores{' '}
                <code>initialValues</code> and clears <code>errors</code> + <code>touched</code> +{' '}
                <code>dirty</code>. Useful for "cancel edit" flows.
              </Text>
              <Text muted>
                No external validation library ships with <code>@osd/ui</code>. For Zod, write a
                thin <code>validate</code> adapter at the consumer site:{' '}
                <code>{`(v) => Schema.safeParse(v).error?.flatten().fieldErrors`}</code>.
              </Text>
            </Stack>
          </Card.Body>
        </Card>
      </Section>

      <Section title="Code examples" hint="copy-pasteable patterns for the common form shapes">
        <Card>
          <Card.Body>
            <Stack gap={5}>
              <CodeBlock
                title="useForm() — uncontrolled inputs (smallest code)"
                hint="inputs carry a name; useForm reads values + errors + touched"
                language="tsx"
                code={`const form = useForm({
  initialValues: { email: '', password: '' },
  validate: (v) => {
    const e: Record<string, string> = {};
    if (!v.email.includes('@')) e.email = 'Enter a valid email.';
    if (v.password.length < 12) e.password = 'At least 12 characters.';
    return e;
  },
  onSubmit: async (values) => api.auth().login(values),
});

return (
  <form onSubmit={form.handleSubmit}>
    <Field label="Email" error={form.touched.email ? form.errors.email : null}>
      <Input
        name="email"
        value={form.values.email}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
        disabled={form.submitting}
      />
    </Field>
    <Field label="Password" error={form.touched.password ? form.errors.password : null}>
      <Input
        type="password"
        name="password"
        value={form.values.password}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
        disabled={form.submitting}
      />
    </Field>
    <Button type="submit" disabled={form.submitting}>Sign in</Button>
  </form>
);`}
              />

              <CodeBlock
                title="Server error + reset"
                hint="set a top-level error, then clear it on reset"
                language="tsx"
                code={`function LoginPage() {
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { email: '', password: '' },
    onSubmit: async (values) => {
      try {
        await api.auth().login(values);
      } catch (err) {
        setServerError(err instanceof Error ? err.message : 'Login failed');
      }
    },
  });

  return (
    <form onSubmit={form.handleSubmit}>
      {serverError !== null && <Alert variant="danger">{serverError}</Alert>}
      <Field label="Email">
        <Input name="email" value={form.values.email} onChange={form.handleChange} />
      </Field>
      <Field label="Password">
        <Input type="password" name="password" value={form.values.password} onChange={form.handleChange} />
      </Field>
      <Button type="submit" disabled={form.submitting}>Sign in</Button>
      <Button type="button" variant="default" onClick={() => { form.reset(); setServerError(null); }}>
        Reset
      </Button>
    </form>
  );
}`}
              />

              <CodeBlock
                title="Zod-backed validate (drop-in adapter)"
                hint="one line — no other change to the form code"
                language="tsx"
                code={`import { z } from 'zod';

const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(12),
});

const form = useForm({
  initialValues: { email: '', password: '' },
  validate: (v) => {
    const r = LoginSchema.safeParse(v);
    return r.success ? {} : r.error.flatten().fieldErrors;
  },
  onSubmit: api.auth().login,
});`}
              />

              <CodeBlock
                title="Fieldset + ErrorSummary at the top of the form"
                hint="GOV.UK pattern — list every error in one place above the form"
                language="tsx"
                code={`function AccountForm() {
  const form = useForm({
    initialValues: { street: '', city: '' },
    onSubmit: api.account().update,
  });

  // Convert useForm errors -> ErrorSummary items.
  const summary = Object.entries(form.errors).map(([field, message]) => ({
    fieldId: field,
    fieldLabel: field,
    message: String(message),
  }));

  return (
    <form onSubmit={form.handleSubmit}>
      {summary.length > 0 && <ErrorSummary errors={summary} />}
      <Fieldset legend="Address">
        <Field label="Street" error={form.touched.street ? form.errors.street : null}>
          <Input name="street" value={form.values.street} onChange={form.handleChange} />
        </Field>
        <Field label="City" error={form.touched.city ? form.errors.city : null}>
          <Input name="city" value={form.values.city} onChange={form.handleChange} />
        </Field>
      </Fieldset>
      <Button type="submit">Save</Button>
    </form>
  );
}`}
              />

              <CodeBlock
                title="ColorPicker with the random-roll variant"
                hint="adds a dice button; click picks a fresh hex via WebCrypto"
                language="tsx"
                code={`import { ColorPicker } from '@osd/ui/color-picker';

function ThemePage() {
  const [accent, setAccent] = useState('#0ea5e9');
  return (
    <ColorPicker
      label="Accent color"
      value={accent}
      onChange={setAccent}
      variant="random"
    />
  );
}`}
              />
            </Stack>
          </Card.Body>
        </Card>
      </Section>
    </Stack>
  );
}

// ── Demos ───────────────────────────────────────────────────────

interface SignupValues {
  email: string;
  password: string;
  agree: boolean;
}

function UncontrolledDemo() {
  const [submitted, setSubmitted] = useState<string | null>(null);
  const form = useForm<SignupValues>({
    initialValues: { email: '', password: '', agree: false },
    validate: (v) => {
      const errors: Partial<Record<keyof SignupValues, string>> = {};
      if (!v.email.includes('@')) errors.email = 'Enter a valid email.';
      if (v.password.length < 12) errors.password = 'At least 12 characters.';
      if (!v.agree) errors.agree = 'You must agree to the terms.';
      return errors;
    },
    onSubmit: async (values) => {
      // Simulate a server roundtrip.
      await new Promise((r) => setTimeout(r, 400));
      setSubmitted(JSON.stringify(values));
    },
  });

  return (
    <Card>
      <Card.Body>
        <form onSubmit={form.handleSubmit} className="osd-login-form" noValidate={false}>
          {form.errors.email !== undefined ||
          form.errors.password !== undefined ||
          form.errors.agree !== undefined ? (
            <Alert variant="danger">Please fix the highlighted fields.</Alert>
          ) : null}
          {submitted !== null ? (
            <Alert variant="success">
              Submitted: <code>{submitted}</code>
            </Alert>
          ) : null}
          <Stack gap={3}>
            <Field label="Email" error={form.touched.email ? form.errors.email : null} required>
              <Input
                name="email"
                type="email"
                value={form.values.email}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                disabled={form.submitting}
              />
            </Field>
            <Field
              label="Password"
              hint="Minimum 12 characters."
              error={form.touched.password ? form.errors.password : null}
              required
            >
              <Input
                name="password"
                type="password"
                value={form.values.password}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                disabled={form.submitting}
              />
            </Field>
            <Checkbox
              name="agree"
              label="I agree to the terms"
              checked={form.values.agree}
              onChange={form.handleChange}
            />
            {form.touched.agree && form.errors.agree !== undefined ? (
              <Text muted>
                <AlertCircle aria-hidden="true" /> {form.errors.agree}
              </Text>
            ) : null}
          </Stack>
          <div className="osd-login-form__actions">
            <Button type="submit" variant="primary" disabled={form.submitting}>
              {form.submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
}

function ControlledDemo() {
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<{ name: string }>({
    initialValues: { name: '' },
    onSubmit: async () => {
      // Pretend the server returned a 400 — set a top-level error.
      setServerError('Server said: name is already taken.');
    },
  });

  return (
    <Card>
      <Card.Body>
        <form onSubmit={form.handleSubmit} className="osd-login-form" noValidate>
          {serverError !== null ? <Alert variant="danger">{serverError}</Alert> : null}
          <Stack gap={3}>
            <Field
              label="Name"
              hint="Controlled via form.values.name"
              error={form.touched.name ? form.errors.name : null}
              required
            >
              <Input
                name="name"
                value={form.values.name}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
            </Field>
          </Stack>
          <div className="osd-login-form__actions">
            <Button type="submit" variant="primary" disabled={form.submitting}>
              Submit
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => {
                form.reset();
                setServerError(null);
              }}
            >
              Reset
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
}
